import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { ParamDataType, ParamCategory } from '@bisa/shared';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { PageSpinner } from '../../components/ui/Spinner';

interface ParamRow {
  paramKey: string;
  globalValue: string;
  tenantValue: string | null;
  dataType: ParamDataType;
  description: string;
  category: ParamCategory;
  isEditable: boolean;
}

function TenantParamRow({
  row,
  facilityId,
  onSaved,
}: {
  row: ParamRow;
  facilityId: string;
  onSaved: (key: string, value: string | null) => void;
}) {
  const effective = row.tenantValue ?? row.globalValue;
  const [value, setValue] = useState(effective);
  const [saving, setSaving] = useState(false);
  const hasOverride = row.tenantValue !== null;
  const dirty = value !== effective;

  const save = async () => {
    setSaving(true);
    if (hasOverride) {
      await apiPut(`/admin/facilities/${facilityId}/params`, { paramKey: row.paramKey, paramValue: value });
    } else {
      await apiPost(`/admin/facilities/${facilityId}/params`, { paramKey: row.paramKey, paramValue: value });
    }
    onSaved(row.paramKey, value);
    setSaving(false);
  };

  const clear = async () => {
    setSaving(true);
    await apiPut(`/admin/facilities/${facilityId}/params`, { paramKey: row.paramKey, paramValue: null });
    setValue(row.globalValue);
    onSaved(row.paramKey, null);
    setSaving(false);
  };

  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <p className="font-mono text-sm font-medium text-gray-900">{row.paramKey}</p>
        <p className="text-xs text-gray-400">{row.description}</p>
      </td>
      <td className="px-4 py-3 text-sm text-gray-400 font-mono">{row.globalValue}</td>
      <td className="px-4 py-3 w-48">
        {row.dataType === ParamDataType.BOOLEAN ? (
          <Select value={value} onChange={(e) => setValue(e.target.value)} disabled={!row.isEditable}>
            <option value="true">true</option>
            <option value="false">false</option>
          </Select>
        ) : (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!row.isEditable}
            type={row.dataType === ParamDataType.INTEGER || row.dataType === ParamDataType.DECIMAL ? 'number' : 'text'}
          />
        )}
      </td>
      <td className="px-4 py-3">
        {hasOverride && (
          <span className="mr-2 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
            Override
          </span>
        )}
      </td>
      <td className="px-4 py-3 flex gap-1">
        {row.isEditable && dirty && (
          <Button size="sm" loading={saving} onClick={save}>Save</Button>
        )}
        {row.isEditable && hasOverride && !dirty && (
          <Button size="sm" variant="ghost" loading={saving} onClick={clear}>Reset to global</Button>
        )}
      </td>
    </tr>
  );
}

export function TenantParamsPage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;

  const [rows, setRows] = useState<ParamRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet<ParamRow[]>(`/admin/facilities/${facilityId}/params`);
      setRows(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (key: string, value: string | null) => {
    setRows((prev) =>
      prev.map((r) => r.paramKey === key ? { ...r, tenantValue: value } : r),
    );
  };

  return (
    <div className="p-6">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-gray-900">Clinic Parameters</h1>
        <p className="text-sm text-gray-500 mt-1">
          Override global defaults for this clinic. Empty = use global default.
        </p>
      </div>

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Key / Description', 'Global Default', 'Clinic Value', 'Status', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <TenantParamRow key={r.paramKey} row={r} facilityId={facilityId} onSaved={handleSaved} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
